/**
 * Error boundary that catches render errors in plugin Renderer components.
 * Displays a friendly fallback instead of crashing the whole app.
 */

import { AlertTriangle, RotateCcw } from "lucide-react";
import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { frontendLog } from "../utils/frontend-logger";

interface Props {
    pluginName: string;
    definitionId: string;
    children: ReactNode;
    /** When this value changes, the error state is automatically cleared.
     *  Use this instead of changing `key` to avoid re-mounting children. */
    resetKey?: string | number;
}

interface State {
    error: Error | null;
    /** Track the last resetKey so we can detect changes in getDerivedStateFromProps */
    prevResetKey?: string | number;
}

export class PluginErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { error };
    }

    static getDerivedStateFromProps(nextProps: Props, prevState: State): Partial<State> | null {
        // If resetKey changed and we have an error, clear it
        if (
            nextProps.resetKey !== undefined &&
            prevState.prevResetKey !== undefined &&
            nextProps.resetKey !== prevState.prevResetKey &&
            prevState.error !== null
        ) {
            return { error: null, prevResetKey: nextProps.resetKey };
        }
        if (nextProps.resetKey !== prevState.prevResetKey) {
            return { prevResetKey: nextProps.resetKey };
        }
        return null;
    }

    componentDidCatch(error: Error, _info: ErrorInfo) {
        frontendLog.error(
            `[PluginErrorBoundary] Plugin "${this.props.pluginName}" (${this.props.definitionId}) crashed`,
            error,
            { source: "plugin" },
        );
    }

    handleReset = () => {
        this.setState({ error: null });
    };

    render() {
        if (this.state.error) {
            return (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 my-2">
                    <div className="flex items-start gap-2">
                        <AlertTriangle
                            size={16}
                            className="text-destructive shrink-0 mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-destructive">
                                Plugin render error
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                <code className="bg-muted px-1 py-0.5 rounded text-[11px]">
                                    {this.props.pluginName}
                                </code>{" "}
                                ({this.props.definitionId})
                            </p>
                            <pre className="mt-2 text-[11px] font-mono text-destructive/80 whitespace-pre-wrap break-all max-h-24 overflow-auto">
                                {this.state.error.message}
                            </pre>
                            <button
                                type="button"
                                className="mt-2 flex items-center gap-1 px-2 py-1 rounded text-xs bg-muted hover:bg-muted/80 transition-colors text-foreground"
                                onClick={this.handleReset}
                            >
                                <RotateCcw size={12} />
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
